import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { usePlumberSearch } from '../hooks/usePlumberSearch';
import { LocationMap } from '../components/LocationMap';
import { SearchHero } from '../components/SearchHero';
import { StickyCallButton } from '../components/StickyCallButton';
import { PlumberCard } from '../components/PlumberCard';
import { SearchFilters } from '../components/SearchFilters';
import { SchemaMarkup } from '../components/SchemaMarkup';
import { supabase } from '../lib/supabase';
import type { SearchFilters as SearchFiltersType } from '../types';

interface LocationInfo {
  city: string;
  state: string;
  state_code: string;
}

function SearchResultsPage() {
  const { zipCode } = useParams<{ zipCode: string }>();
  const [filters, setFilters] = useState<SearchFiltersType>({});
  const [locationInfo, setLocationInfo] = useState<LocationInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [stateCities, setStateCities] = useState<Array<{
    city: string;
    state: string;
    state_code: string;
    slug: string;
    zip_code: string;
  }>>([]);
  const [nearbyAreas, setNearbyAreas] = useState<Array<{
    city: string;
    state: string;
    zip_code: string;
    latitude: number;
    longitude: number;
    key: string;
  }>>([]);
  const navigate = useNavigate();
  
  const {
    plumbers,
    loading: plumbersLoading,
    error,
    totalCount,
    currentPage,
    setCurrentPage,
    itemsPerPage
  } = usePlumberSearch(zipCode || '', filters);

  useEffect(() => {
    async function fetchLocationInfo() {
      if (!zipCode) return;

      setLoading(true);
      
      try {
        const { data: cityData, error: cityError } = await supabase
          .from('cities')
          .select(`
            id,
            name, 
            state:state_id!inner(
              id,
              name,
              code
            ),
            zip_codes
          `)
          .filter('zip_codes', 'cs', `{${zipCode}}`)
          .limit(1);

        if (cityError) throw cityError;
        
        if (cityData && cityData.length > 0) {
          const city = cityData[0];
          setLocationInfo({
            city: city.name,
            state: city.state.name,
            state_code: city.state.code
          });
        } else {
          // If no exact match found, try to find nearby cities
          const { data: nearbyData, error: nearbyError } = await supabase
            .from('cities')
            .select(`
              id,
              name,
              state:state_id!inner(
                id,
                name,
                code
              ),
              zip_codes
            `)
            .order('created_at')
            .limit(1);

          if (nearbyError) throw nearbyError;

          if (nearbyData && nearbyData.length > 0) {
            const nearbyCity = nearbyData[0];
            setLocationInfo({
              city: nearbyCity.name,
              state: nearbyCity.state.name,
              state_code: nearbyCity.state.code
            });
          } else {
            throw new Error('No service areas found for this ZIP code');
          }
        }
      } catch (error) {
        console.error('Error fetching location info:', error);
        const message = error instanceof Error ? error.message : 'Failed to find location';
        toast.error(message);
        setLocationInfo(null);
      } finally {
        setLoading(false);
      }
    }
    fetchLocationInfo();
  }, [zipCode]);

  // Load nearby areas
  useEffect(() => {
    async function loadNearbyAreas() {
      if (!zipCode) return;
      
      try {
        const { data: cityData } = await supabase
          .from('cities')
          .select(`
            name,
            state:state_id(code),
            zip_codes,
            latitude,
            longitude
          `)
          .not('zip_codes', 'cs', `{${zipCode}}`);

        if (cityData) {
          // Create areas with unique keys
          const areas = cityData.flatMap(city =>
            city.zip_codes.map(zip => ({
              city: city.name,
              state: city.state.code,
              zip_code: zip,
              latitude: city.latitude,
              longitude: city.longitude,
              key: `${city.name}-${city.state.code}`
            }))
          );
          
          // Deduplicate areas by key and take first ZIP code for each city
          const uniqueAreas = Array.from(
            new Map(
              areas.map(area => [area.key, area])
            ).values()
          );
          
          // Sort by city name
          uniqueAreas.sort((a, b) => a.city.localeCompare(b.city));
          
          setNearbyAreas(uniqueAreas);
        }
      } catch (error) {
        console.error('Error loading nearby areas:', error);
      }
    }

    loadNearbyAreas();
  }, [zipCode]);

  // Load cities from the same state
  useEffect(() => {
    async function loadStateCities() {
      if (!locationInfo?.state_code) return;

      try {
        // Get all cities from the same state
        const { data: cities, error } = await supabase
          .from('cities')
          .select(`
            name,
            slug,
            zip_codes,
            state:state_id!inner(code, name)
          `)
          .eq('state.code', locationInfo.state_code)
          .neq('zip_codes', `{${zipCode}}`); // Exclude current city

        if (error) throw error;

        if (cities) {
          // Map cities to the required format
          const mappedCities = cities.map(city => ({
            city: city.name,
            state: city.state.name,
            state_code: city.state.code,
            slug: city.slug,
            zip_code: city.zip_codes[0]
          }));

          // Shuffle and take 8 random cities
          const randomCities = mappedCities
            .sort(() => Math.random() - 0.5)
            .slice(0, 8);

          setStateCities(randomCities);
        }
      } catch (error) {
        console.error('Error loading cities:', error);
      }
    }
    loadStateCities();
  }, [locationInfo?.state_code, zipCode]);

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  if (loading || plumbersLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl text-gray-600">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl text-red-600">Failed to load plumbers. Please try again.</div>
      </div>
    );
  }

  // Calculate map center based on first service area
  const mapCenter = plumbers[0]?.service_areas[0]
    ? [
        plumbers[0].service_areas[0].latitude,
        plumbers[0].service_areas[0].longitude
      ] as [number, number]
    : [39.8283, -98.5795] as [number, number]; // Center of USA

  return (
    <>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>
          {locationInfo 
            ? `Plumbers in ${locationInfo.city}, ${locationInfo.state_code} (${zipCode}) - Plumber.Guru`
            : `Plumbers near ${zipCode} - Plumber.Guru`}
        </title>
        <meta name="robots" content="index, follow" />
        <meta
          name="description"
          content={locationInfo 
            ? `Find trusted local plumbers in ${locationInfo.city}, ${locationInfo.state_code} ${zipCode}. Available 24/7 for emergency plumbing services. Licensed, insured plumbers with fair pricing.`
            : `Find trusted local plumbers in ${zipCode}. Compare ratings, reviews, and services.`}
        />
        {/* Open Graph / Facebook */}
        <meta name="keywords" content={`plumber ${zipCode}, emergency plumber ${zipCode}, ${locationInfo?.city} plumber, plumbing services ${locationInfo?.city}, 24/7 plumber ${zipCode}, licensed plumber ${locationInfo?.city}`} />
        <meta property="og:title" content={locationInfo ? `Plumbers in ${locationInfo.city}, ${locationInfo.state_code} (${zipCode})` : `Plumbers near ${zipCode}`} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://plumbers.guru/search/${zipCode}`} />
        <meta property="og:description" content={locationInfo 
          ? `Find trusted local plumbers in ${locationInfo.city}, ${locationInfo.state_code}. Available 24/7 for emergency plumbing services.`
          : `Find trusted local plumbers in ${zipCode}. Compare ratings, reviews, and services.`} />
        <meta property="og:image" content="https://plumbers.guru/og-image.jpg" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={locationInfo ? `Plumbers in ${locationInfo.city}, ${locationInfo.state_code}` : `Plumbers near ${zipCode}`} />
        <meta name="twitter:description" content={locationInfo 
          ? `Find trusted local plumbers in ${locationInfo.city}, ${locationInfo.state_code}. Available 24/7 for emergency plumbing services.`
          : `Find trusted local plumbers in ${zipCode}. Compare ratings, reviews, and services.`} />
        <meta name="twitter:image" content="https://plumbers.guru/og-image.jpg" />
        
        {/* Additional SEO */}
        <meta name="geo.region" content={locationInfo?.state_code} />
        <meta name="geo.placename" content={locationInfo?.city} />
      </Helmet>

      {locationInfo && (
        <SchemaMarkup
          type="Service"
          data={{
            title: `Plumbing Services in ${locationInfo.city}, ${locationInfo.state_code}`,
            description: `Professional plumbing services in ${locationInfo.city}, ${locationInfo.state_code}. Available 24/7 for emergencies.`,
            city: locationInfo.city,
            city_id: zipCode
          }}
        />
      )}

      <div className="bg-gray-50 min-h-screen py-8">
        <SearchHero 
          city={locationInfo?.city}
          state={locationInfo?.state_code}
          totalResults={totalCount}
        />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Filters */}
            <div className="lg:sticky lg:top-4 lg:h-fit">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Filter Results</h2>
              <SearchFilters
                filters={filters}
                onFilterChange={setFilters}
              />
            </div>

            {/* Results */}
            <div className="lg:col-span-3 space-y-8">
              {/* Results Count */}
              <div className="bg-white p-4 rounded-lg shadow" role="status" aria-live="polite">
                <p className="text-gray-600">
                  Found <span className="font-semibold text-gray-900">{totalCount}</span> plumbers serving{' '}
                  {locationInfo ? `${locationInfo.city}, ${locationInfo.state_code}` : `ZIP code ${zipCode}`}
                </p>
              </div>

              {/* Map */}
              <LocationMap
                center={mapCenter}
                zoom={12}
                markers={[
                  ...plumbers.flatMap(p => p.service_areas.map(area => ({
                    position: [area.latitude, area.longitude] as [number, number],
                    popup: `${p.business_name}<br/>${area.city}, ${area.state}`,
                    zipCode: area.zip_code
                  }))),
                  ...nearbyAreas.map(area => ({
                    position: [area.latitude, area.longitude] as [number, number],
                    popup: `${area.city}, ${area.state}<br/>ZIP: ${area.zip_code}`,
                    zipCode: area.zip_code
                  }))
                ]}
                onMarkerClick={(zipCode) => navigate(`/search/${zipCode}`)}
              />

              {/* Plumber Cards */}
              {plumbers.length > 0 ? (
                <div className="space-y-6">
                  {plumbers.map((plumber, index) => (
                    <PlumberCard 
                      key={`${plumber.id}-${index}`} 
                      plumber={plumber} 
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 bg-white rounded-lg shadow">
                  <h2 className="text-xl font-semibold text-gray-900 mb-2">
                    No plumbers found
                  </h2>
                  <p className="text-gray-600">
                    Try adjusting your filters or searching a different ZIP code
                  </p>
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <nav aria-label="Pagination" className="flex justify-center space-x-2 mt-8">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    aria-label="Previous page"
                    className="px-4 py-2 border rounded-md disabled:opacity-50"
                  >
                    Previous
                  </button>
                  {[...Array(totalPages)].map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setCurrentPage(i + 1)}
                      aria-label={`Go to page ${i + 1}`}
                      aria-current={currentPage === i + 1 ? 'page' : undefined}
                      className={`px-4 py-2 border rounded-md ${
                        currentPage === i + 1
                          ? 'bg-blue-600 text-white'
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    aria-label="Next page"
                    className="px-4 py-2 border rounded-md disabled:opacity-50"
                  >
                    Next
                  </button>
                </nav>
              )}
            </div>
          </div>
        </div>

        {/* Popular Service Areas */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 bg-white mt-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            {locationInfo 
              ? `More Cities in ${locationInfo.state}${stateCities.length === 0 ? ' Coming Soon' : ''}`
              : 'Popular Service Areas'
            }
          </h2>
          {stateCities.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {stateCities.map(city => (
                <Link
                  key={`${city.state_code}-${city.slug}`}
                  to={`/${city.state_code.toLowerCase()}/${city.slug}/${city.zip_code}`}
                  className="text-blue-600 hover:text-blue-800"
                >
                  {city.city}
                </Link>
              ))}
            </div>
          ) : locationInfo ? (
            <p className="text-gray-600 text-center">
              We're expanding our service areas in {locationInfo.state}. Check back soon for more cities!
            </p>
          ) : null}
        </div>

        {/* Sticky Call Button */}
        <StickyCallButton />
      </div>
    </>
  );
}

export default SearchResultsPage;

export { SearchResultsPage }